import React, { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";
import Webcam from "react-webcam";
import workerAPI from "../api/workerAPI";

const FaceRegistration = ({ onSuccess }) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [isModelsLoaded, setIsModelsLoaded] = useState(false);
  const [message, setMessage] = useState("");

  // Load face-api.js models on mount
  useEffect(() => {
    const loadModels = async () => {
      setMessage("Loading face-api models...");
      const MODEL_URL = process.env.PUBLIC_URL + "/models";

      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri(`${MODEL_URL}/tiny_face_detector`);
        await faceapi.nets.faceLandmark68Net.loadFromUri(`${MODEL_URL}/face_landmark_68`);
        await faceapi.nets.faceRecognitionNet.loadFromUri(`${MODEL_URL}/face_recognition`);
        await faceapi.nets.ssdMobilenetv1.loadFromUri(`${MODEL_URL}/ssd_mobilenetv1`);
      //  const MODEL_URL = "./models";

      // try {
      //   await Promise.all([
      //     faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      //     faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      //     faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      //     faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
        //]);

        setIsModelsLoaded(true);
        setMessage("Models loaded. Ready to scan.");
      } catch (err) {
        console.error("Model load error:", err);
        setMessage("Error loading models.");
      }
    };

    loadModels();
  }, []);

  // Distance function
  const euclideanDistance = (d1, d2) => {
    return Math.sqrt(d1.reduce((sum, val, i) => sum + Math.pow(val - d2[i], 2), 0));
  };

  const handleRegisterFace = async () => {
    if (!isModelsLoaded) {
      setMessage("Models are not loaded yet.");
      return;
    }

    const video = webcamRef.current.video;
    if (!video || video.readyState !== 4) {
      setMessage("Webcam not ready.");
      return;
    }

    const displaySize = { width: video.videoWidth, height: video.videoHeight };
    faceapi.matchDimensions(canvasRef.current, displaySize);

    setMessage("Scanning for face...");
    const detection = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();

    const context = canvasRef.current.getContext("2d");
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    if (detection) {
      const resizedDetection = faceapi.resizeResults(detection, displaySize);
      faceapi.draw.drawDetections(canvasRef.current, resizedDetection);
      faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetection);

      const descriptor = Array.from(detection.descriptor);

      try {
        const res = await workerAPI.get('/descriptors/all');
        const existing = res.data.filter(w => Array.isArray(w.faceDescriptor));

        const isDuplicate = existing.some((item) => {
          const distance = euclideanDistance(descriptor, item.faceDescriptor);
          return distance < 0.45; // adjust if needed
        });

        if (isDuplicate) {
          setMessage("Face already registered.");
          alert("❌ This face is already registered.");
          return;
        }

        alert("✅ Face registered successfully!");
        setMessage("Face captured. You can now save.");

        if (onSuccess) {
          onSuccess(descriptor); // Pass descriptor to parent component
        }
      } catch (err) {
        console.error("Descriptor check failed:", err);
        alert("❌ Error checking existing faces.");
      }
    } else {
      setMessage("No face detected. Try again.");
      alert("❌ No face detected. Please try again.");
    }
  };

  return (
    <div className="flex flex-col items-center p-4">
      <h2 className="text-2xl font-bold mb-4">Register Face</h2>

      <div className="relative border-2 border-gray-400 rounded-lg overflow-hidden">
        <Webcam
          ref={webcamRef}
          style={{ width: 640, height: 480 }}
          mirrored
        />
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 640,
            height: 480,
          }}
        />
      </div>

      <button
        onClick={handleRegisterFace}
        className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700"
        disabled={!isModelsLoaded}
      >
        {isModelsLoaded ? "Scan & Capture Face" : "Loading Models..."}
      </button>

      {message && (
        <p className="mt-4 text-center text-lg text-gray-700">{message}</p>
      )}
    </div>
  );
};

export default FaceRegistration;
